import keyCodes from '@honda-canada/js-utilities/lib/keyCodes';
import keypressCallback from '@honda-canada/js-utilities/lib/keypressCallback';
import React, { useContext, useLayoutEffect, useRef, useState } from 'react';
import { Box, Optional } from '@honda-canada/design-system-react';

import AccordionItemTitleIcon from './AccordionItemTitleIcon';
import HeaderContext from '../service/HeaderContext';
import formatDataTestId from '../../../utils/formatDataTestId';
import themeStyles from './Accordion.styles';

const AccordionItemContainer = themeStyles.apply(Box, 'AccordionItemContainer');
const AccordionItemContentContainer = themeStyles.apply(Box, 'AccordionItemContentContainer');
const AccordionItemTitle = themeStyles.apply(Box, 'AccordionItemTitle');
const AccordionItemStyle = themeStyles.apply(Box, 'AccordionItem');

const AccordionItemContent = ({ isActive, children, level }) => {
  const { styles } = useContext(HeaderContext);
  const [maxHeight, setMaxHeight] = useState(0);
  // I destroy on Close event all content to collapse child Accordions
  const [renderContent, setRenderContent] = useState(null);
  const [showChildren, setShowChildren] = useState(isActive);
  const animatedAccordionRef = useRef(null);

  useLayoutEffect(() => {
    if (isActive) {
      setMaxHeight(animatedAccordionRef.current?.scrollHeight);
      setShowChildren(true);
      setRenderContent(children);
      const timer = setTimeout(() => setMaxHeight('none'), 200);
      return () => clearTimeout(timer);
    }
    setMaxHeight(animatedAccordionRef.current?.scrollHeight);
    const timer = setTimeout(() => {
      setMaxHeight(0);
      const alttimer = setTimeout(() => {
        setShowChildren(false);
        setRenderContent(null);
      }, 200);
      return () => clearTimeout(alttimer);
    }, 200);
    return () => clearTimeout(timer);
  }, [isActive]);

  return (
    <AccordionItemContentContainer
      aria-live="polite"
      className="AccordionItemContentContainer"
      data-testid="header-accordion-item-content-container"
      level={level}
      maxHeight={maxHeight}
      onKeyDown={e => e.stopPropagation()}
      ref={animatedAccordionRef}
      showChildren={showChildren}
      styles={styles.accordion.contentContainer[level]}
    >
      {renderContent}
    </AccordionItemContentContainer>
  );
};

const AccordionItem = ({
  isActive,
  toggleItem,
  titleComponent,
  content,
  level,
  iconName = 'animatedArrowUpDown', // arrowRight
  itemLabel,
  isLastItem,
  hasDealerItem,
}) => {
  const { styles } = useContext(HeaderContext);
  const iconColor = isActive ? styles.accordion.item[level].iconColor : 'black';

  return (
    <AccordionItemStyle
      className="AccordionItem"
      styles={styles.accordion.item[level]}
      data-testid={`header-mobile${formatDataTestId(itemLabel)}-subnav`}
      isLastItem={isLastItem}
      hasDealerItem={hasDealerItem}
    >
      <AccordionItemContainer
        className="AccordionItemContainer"
        data-testid="header-accordion-item-container"
        isActive={isActive}
        tabIndex={0}
        aria-pressed={isActive}
        aria-expanded={isActive}
        onKeyDown={keypressCallback(keyCodes.ENTER, toggleItem)}
        as="section"
      >
        <Optional when={!!content}>
          <AccordionItemTitle
            className="AccordionItemTitle"
            onClick={toggleItem}
            onKeyDown={toggleItem}
            onMouseDown={e => {
              e.preventDefault();
            }}
            styles={styles.accordion.title[level]}
            isActive={isActive}
            data-testid={`header-mobile-drawer-item${formatDataTestId(itemLabel)}`}
          >
            {titleComponent}
            <AccordionItemTitleIcon iconColor={iconColor} iconName={iconName} isActive={isActive} />
          </AccordionItemTitle>
          <AccordionItemContent isActive={isActive} level={level}>
            {content}
          </AccordionItemContent>
        </Optional>
        <Optional when={!content}>
          <AccordionItemTitle className="AccordionItemTitle" styles={styles.accordion.title[level]}>
            {titleComponent}
          </AccordionItemTitle>
        </Optional>
      </AccordionItemContainer>
    </AccordionItemStyle>
  );
};

export default AccordionItem;
